import { Link } from "react-router-dom";
import { Package, RotateCcw, ShieldCheck, Truck } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, SectionTag } from "../components/bits";

const SECTIONS = [
  {
    icon: Truck,
    title: "Free express shipping, AU-wide",
    points: [
      "Every order ships free via express post, anywhere in Australia — no minimum spend, no hidden fees at checkout.",
      "Orders are dispatched from our Australian warehouse within 1–2 business days.",
      "Most deliveries arrive within 2–5 business days of dispatch, depending on your postcode.",
      "Tracking is emailed to you as soon as your light leaves the warehouse.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "2-year Australian warranty",
    points: [
      "Every SuperTechLED fixture is covered for 2 years from the delivery date — diodes, drivers, wiring and build quality.",
      "Warranty claims are handled locally in Australia: no overseas RMA depots and no months-long waits.",
      "If a fault is confirmed, we repair or replace the unit and cover the freight both ways.",
      "Keep your order confirmation email as proof of purchase — that's all we need.",
    ],
  },
  {
    icon: RotateCcw,
    title: "30-day hassle-free returns",
    points: [
      "Changed your mind or the light doesn't suit your space? You have 30 days from delivery to return it.",
      "The fixture just needs to be complete and in reasonable condition — original packaging preferred but not mandatory.",
      "Email us with your order number and we'll arrange the return and process your refund to the original payment method.",
    ],
  },
  {
    icon: Package,
    title: "Packaging & discretion",
    points: [
      "Lights ship in plain, sturdy outer cartons with no branding on the outside.",
      "Every unit is foam-crated to survive the couriers, and dims are set and tested before dispatch.",
    ],
  },
];

export default function Shipping() {
  return (
    <div className="mx-auto max-w-3xl px-5 pb-28 pt-28 sm:px-8" data-testid="shipping-page">
      <SEO
        title="Shipping, Warranty & Returns — Free Express AU-wide | SuperTechLED"
        description="Free express shipping Australia-wide on every SuperTechLED order, dispatch within 1–2 business days, a 2-year Australian warranty handled locally, and 30-day hassle-free returns."
      />
      <Reveal>
        <SectionTag>The fine print, minus the fine print</SectionTag>
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Shipping, warranty &amp; returns
        </h1>
        <p className="mt-4 max-w-xl text-sm text-[#9FB8A8] sm:text-base">
          Everything about getting your light, what's covered, and what happens if you change your mind.
        </p>
      </Reveal>

      <div className="mt-12 space-y-6">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={Math.min(i * 0.06, 0.3)}>
            <section className="rounded-3xl border border-[#39FF14]/15 bg-[#071009] p-7 sm:p-9" data-testid={`shipping-section-${i}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#39FF14]/25 bg-[#0A150F]">
                  <s.icon className="h-5 w-5 text-[#39FF14]" />
                </span>
                <h2 className="font-display text-lg font-bold text-[#F0FDF4] sm:text-xl">{s.title}</h2>
              </div>
              <ul className="mt-5 space-y-2.5 text-sm leading-relaxed text-[#9FB8A8]">
                {s.points.map((pt) => (
                  <li key={pt} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#39FF14]" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-6 rounded-2xl border border-[#39FF14]/20 bg-[#0A150F] p-6 sm:p-7" data-testid="acl-notice">
        <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Australian Consumer Law</div>
        <p className="mt-3 text-sm leading-relaxed text-[#C9DCD0]">
          Our goods come with guarantees that cannot be excluded under the Australian Consumer Law. You are entitled
          to a replacement or refund for a major failure and compensation for any other reasonably foreseeable loss or
          damage. You are also entitled to have the goods repaired or replaced if the goods fail to be of acceptable
          quality and the failure does not amount to a major failure.
        </p>
      </Reveal>

      <Reveal className="mt-12 text-center">
        <p className="text-sm text-[#9FB8A8]">
          {`Questions about an order or a warranty claim? `}
          <a href="mailto:supertechled@outlook.com" className="text-[#39FF14] hover:text-[#86EFAC]">Email supertechled@outlook.com</a>
          {` or visit our `}
          <Link to="/contact" className="text-[#39FF14] hover:text-[#86EFAC]">contact page</Link>.
        </p>
      </Reveal>
    </div>
  );
}
